import React from 'react';
import Link from 'next/link';
import { TIERS, PILLAR_LABELS } from '../constants';
import { PillarScores } from '../types';
import { getTierSummary, getKeyInsights } from '../utils';
import AssessmentFooter from '../AssessmentFooter';
import { TrendIcon, InfoIcon } from '../icons';
import SpiderChart from '../SpiderChart';
import { NewLogo } from '@/components/ui/icons/Logo';
import { useAssessmentContent } from '../content/context';

interface ResultsScreenProps {
  score: number;
  tier: string;
  pillar: PillarScores;
  firstName: string;
  onCta: () => void;
}

const ResultsScreen: React.FC<ResultsScreenProps> = ({ score, tier, pillar, firstName, onCta }) => {
  const content = useAssessmentContent();
  const { results, pillars, tiers } = content;
  const summary = getTierSummary(score, content);
  const insights = getKeyInsights(pillar, content);

  return (
    <div className="animate-fade-in">
      <nav
        className="bg-white border-b border-a-border px-10 flex items-center justify-between sticky top-0 z-50"
        style={{ height: '58px' }}
      >
        <Link href="/">
          <NewLogo height="60" title="Foxbox Digital" />
        </Link>
        <div className="font-medium text-a-text-muted" style={{ fontSize: '11px' }}>
          {results.navLabel}
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-a-navy flex flex-wrap items-start gap-8 py-8 px-6 sm:gap-10 sm:py-11 sm:px-14">
        {/* Score box */}
        <div
          className="text-center flex-shrink-0"
          style={{
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '10px',
            padding: '24px 20px',
            boxSizing: 'border-box',
            minWidth: '148px',
            flexShrink: 0,
          }}
        >
          <div className="text-a-orange font-extrabold font-sans leading-none" style={{ fontSize: '60px' }}>
            {score}
          </div>
          <div
            style={{
              fontSize: '11px',
              color: 'rgba(255,255,255, 1)',
              marginTop: '4px',
              marginBottom: '16px',
            }}
          >
            {results.outOfLabel}
          </div>
          <div
            className="bg-a-orange text-white font-bold font-sans uppercase inline-block"
            style={{
              fontSize: '10px',
              padding: '5px 12px',
              borderRadius: '20px',
              letterSpacing: '0.06em',
              whiteSpace: 'nowrap',
            }}
          >
            {tier}
          </div>
        </div>

        {/* Copy */}
        <div className="flex-1" style={{ paddingTop: '4px' }}>
          <div
            className="text-a-orange font-bold uppercase"
            style={{
              fontSize: '9px',
              letterSpacing: '0.12em',
              marginBottom: '10px',
            }}
          >
            {results.eyebrow}
          </div>
          <h2
            className="font-extrabold text-white font-sans mt-0"
            style={{
              fontSize: '24px',
              lineHeight: '1.25',
              fontWeight: 800,
              marginBottom: '12px',
            }}
          >
            {results.title}
            {firstName ? `, ${firstName}` : ''}.
          </h2>
          <p
            className="font-sans text-white"
            style={{
              fontSize: '13px',
              lineHeight: '1.7',
              fontWeight: 300,
              marginBottom: '24px',
              maxWidth: '480px',
            }}
          >
            {summary}
          </p>
          <div className="flex flex-wrap" style={{ gap: '10px' }}>
            <button
              className="bg-a-orange text-white font-bold font-sans cursor-pointer hover:bg-a-orange-dark border-0"
              style={{
                fontSize: '12px',
                fontWeight: 700,
                padding: '12px 24px',
                borderRadius: '6px',
                border: 'none',
                letterSpacing: '0.04em',
                transition: 'background 0.15s',
                outline: 'none',
                boxShadow: 'none',
              }}
              onClick={onCta}
            >
              {results.ctaPrimary}
            </button>
            <button
              className="bg-transparent font-sans cursor-pointer border-0"
              style={{
                fontSize: '12px',
                padding: '12px 24px',
                color: 'rgba(255,255,255,0.7)',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.2)',
                fontWeight: 500,
                transition: 'all 0.15s',
                outline: 'none',
                boxShadow: 'none',
              }}
              onClick={onCta}
            >
              {results.ctaSecondary}
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bg-a-off-white grid grid-cols-1 gap-6 p-6 sm:grid-cols-2 sm:gap-6 sm:py-9 sm:px-14">
        {/* Spider chart */}
        <div className="bg-white border border-a-border p-6" style={{ borderRadius: '10px' }}>
          <h4
            className="font-bold text-a-navy uppercase border-b border-a-light-gray"
            style={{
              fontSize: '11px',
              letterSpacing: '0.06em',
              marginTop: 0,
              marginBottom: '20px',
              paddingBottom: '12px',
            }}
          >
            {results.spiderTitle}
          </h4>
          <div className="flex justify-center items-center">
            <SpiderChart pillar={pillar} />
          </div>
          <div className="flex gap-4 justify-center" style={{ marginTop: '14px' }}>
            <div className="flex items-center gap-2" style={{ fontSize: '10px', color: '#9aa3af' }}>
              <div className="bg-a-orange rounded" style={{ width: '16px', height: '3px' }} />
              {results.legendYour}
            </div>
            <div className="flex items-center gap-2" style={{ fontSize: '10px', color: '#9aa3af' }}>
              <div
                style={{
                  width: '16px',
                  height: 0,
                  borderTop: '2px dashed #9aa3af',
                }}
              />
              {results.legendPeer}
            </div>
          </div>
        </div>

        {/* Pillar bars */}
        <div className="bg-white border border-a-border p-6" style={{ borderRadius: '10px' }}>
          <h4
            className="font-bold text-a-navy uppercase border-b border-a-light-gray"
            style={{
              fontSize: '11px',
              letterSpacing: '0.06em',
              marginTop: 0,
              marginBottom: '20px',
              paddingBottom: '12px',
            }}
          >
            {results.pillarBreakdownTitle}
          </h4>
          <div>
            {PILLAR_LABELS.map(({ key }, i) => (
              <div
                key={key}
                style={{
                  marginBottom: i === PILLAR_LABELS.length - 1 ? 0 : '14px',
                }}
              >
                <div className="flex justify-between" style={{ marginBottom: '5px' }}>
                  <span className="font-semibold text-a-text-primary" style={{ fontSize: '11px' }}>
                    {pillars[i]}
                  </span>
                  <span className="font-bold text-a-orange" style={{ fontSize: '11px' }}>
                    {Math.round(pillar[key])}%
                  </span>
                </div>
                <div className="bg-a-light-gray overflow-hidden" style={{ height: '7px', borderRadius: '4px' }}>
                  <div
                    className="bg-a-orange h-full"
                    style={{
                      width: `${Math.round(pillar[key])}%`,
                      borderRadius: '4px',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tier ladder — full width */}
        <div className="bg-white border border-a-border p-6" style={{ borderRadius: '10px', gridColumn: '1 / -1' }}>
          <h4
            className="font-bold text-a-navy uppercase pb-3 mb-5 border-b border-a-light-gray"
            style={{ fontSize: '11px', letterSpacing: '0.06em', marginTop: 0 }}
          >
            {results.tierTitle}
          </h4>
          <div className="flex gap-2">
            {TIERS.map((t, i) => {
              const active = score >= t.min && score <= t.max;
              return (
                <div
                  key={t.label}
                  className="flex-1 text-center"
                  style={{
                    padding: '12px 10px',
                    borderRadius: '7px',
                    background: active ? '#fff7f2' : '#edf0f4',
                    border: `1.5px solid ${active ? '#f88143' : 'transparent'}`,
                  }}
                >
                  <div
                    className="font-bold"
                    style={{
                      fontSize: '10px',
                      color: active ? '#f88143' : '#9aa3af',
                    }}
                  >
                    {tiers[i]?.label ?? t.label}
                  </div>
                  <div className="text-a-text-muted mt-1" style={{ fontSize: '9px' }}>
                    {tiers[i]?.range ?? t.range}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Key insights — full width */}
        <div className="bg-white border border-a-border p-6" style={{ borderRadius: '10px', gridColumn: '1 / -1' }}>
          <h4
            className="font-bold text-a-navy uppercase pb-3 mb-5 border-b border-a-light-gray"
            style={{ fontSize: '11px', letterSpacing: '0.06em', marginTop: 0 }}
          >
            {results.insightsTitle}
          </h4>
          <div className="flex flex-col gap-3">
            {insights.map((ins, i) => (
              <div
                key={i}
                className="flex gap-4"
                style={{
                  padding: '14px 16px',
                  background: '#f5f6f8',
                  borderRadius: '7px',
                  borderLeft: '3px solid #f88143',
                }}
              >
                <div
                  className="flex items-center justify-center flex-shrink-0"
                  style={{
                    width: '28px',
                    height: '28px',
                    background: 'rgba(248,129,67,0.12)',
                    borderRadius: '6px',
                  }}
                >
                  {i === 0 ? <TrendIcon /> : <InfoIcon />}
                </div>
                <div>
                  <strong className="block text-a-navy font-bold mb-1" style={{ fontSize: '12px' }}>
                    {ins.title}
                  </strong>
                  <span className="text-a-text-secondary" style={{ fontSize: '11px', lineHeight: '1.55' }}>
                    {ins.body}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AssessmentFooter />
    </div>
  );
};

export default ResultsScreen;
